import { ToggleValue } from 'components/Admin/Shared/Toggle'
import { createContext, useEffect, useState } from 'react'
import { settingsTotoggleValue } from '../util'
import { useAffiliate } from 'hooks/useAffiliate'

export type ManagedPageValue = {
  value: any
  toggleValue?: ToggleValue
  description?: string
  secondaryDescription?: string
  toggleFn?: (toggleValue: ToggleValue) => void
  updateFn?: (newValue: any) => void
}

export type State = {
  get: ManagedPageValue
  set: React.Dispatch<React.SetStateAction<ManagedPageValue>>
}

export type DirectoryPageType = {
  email: State
  website: State
  address: State
  phone: State
  coachCredentials: State
  yearsAffiliated: State
  bio: State
  primaryImageUrl: State
  galleryImages: State
  scheduleImageUrl: State
  logoImageUrl: State
  location: State
  staff: State
}

export type DirectoryPageContext = {
  directoryPage: DirectoryPageType
}

export function useDirectoryPage(): DirectoryPageType {
  const {
    selectedAffiliate: {
      affiliateId,
      contactEmail,
      web,
      contactPhone,
      address,
      city,
      stateCode,
      countryCode,
      settings,
      members,
      effectiveDate,
      bio,
      images,
      lat,
      lng,
    },
  } = useAffiliate()

  useEffect(() => {
    setEmail({
      value: contactEmail,
      toggleValue: settingsTotoggleValue(settings, 'contactEmail'),
      toggleFn: (toggleValue: ToggleValue) => {
        setEmail({
          ...emailState,
          toggleValue: toggleValue,
        })
      },
      updateFn: (newValue: any) => {
        setEmail({ ...emailState, value: newValue })
      },
    })
    setWebsiteState({
      value: web,
      toggleValue: settingsTotoggleValue(settings, 'web'),
      toggleFn: (toggleValue: ToggleValue) => {
        setWebsiteState({
          ...websiteState,
          toggleValue: toggleValue,
        })
      },
      updateFn: (newValue: any) => {
        setWebsiteState({ ...websiteState, value: newValue })
      },
    })
    setAddressState({
      value: address,
      toggleValue: settingsTotoggleValue(settings, 'address'),
      description:
        city +
        ', ' +
        (stateCode != 'Na' && stateCode != null ? stateCode : countryCode),
      toggleFn: (toggleValue: ToggleValue) => {
        setAddressState({
          ...addressState,
          toggleValue: toggleValue,
        })
      },
    })
    setPhone({
      value: contactPhone,
      toggleValue: settingsTotoggleValue(settings, 'contactPhone'),
      toggleFn: (toggleValue: ToggleValue) => {
        setPhone({
          ...phoneState,
          toggleValue: toggleValue,
        })
      },
      updateFn: (newValue: any) => {
        setPhone({ ...phoneState, value: newValue })
      },
    })
    setCoachCredentials({
      value: members,
      toggleValue: settingsTotoggleValue(settings, 'certifications'),
      toggleFn: (toggleValue: ToggleValue) => {
        setCoachCredentials({
          ...coachCredentials,
          toggleValue: toggleValue,
        })
      },
    })
    setYearsAffiliated({
      value: new Date().getFullYear() - new Date(effectiveDate).getFullYear(),
      toggleValue: settingsTotoggleValue(settings, 'yearsAffiliated'),
      toggleFn: (toggleValue: ToggleValue) => {
        setYearsAffiliated({
          ...yearsAffiliated,
          toggleValue: toggleValue,
        })
      },
    })
    setBioState({
      value: bio,
      updateFn: (newValue: any) => {
        setBioState({ ...bioState, value: newValue })
      },
    })
    setPrimaryImageUrlState({
      value: images?.primary?.url,
      updateFn: (newValue: any) => {
        setPrimaryImageUrlState({ ...priamryImageUrlState, value: newValue })
      },
    })
    setGalleryImagesState({
      value:
        images?.gallery && images?.gallery.length
          ? [...images.gallery]?.sort((a, b) => a.order - b.order)
          : null,
      updateFn: (newValue: any) => {
        setGalleryImagesState({ ...galleryImagesState, value: newValue })
      },
    })
    setLocationState({
      value: { lat, lng },
      updateFn: (newValue: any) => {
        setLocationState({ ...locationState, value: newValue })
      },
    })
    setScheduleImageUrlState({
      value: images?.schedule?.url,
      updateFn: (newValue: any) => {
        setScheduleImageUrlState({ ...scheduleImageUrlState, value: newValue })
      },
    })
    setLogoImageUrlState({
      value: images?.logo?.url,
      updateFn: (newValue: any) => {
        setLogoImageUrlState({ ...logoImageUrlState, value: newValue })
      },
    })
    setStaffState({
      value: members?.sort((a, b) =>
        (a.firstName + a.lastName).toLowerCase() >
        (b.firstName + b.lastName).toLowerCase()
          ? -1
          : 1,
      ),
      updateFn: (newValue: any) => {
        setStaffState({ ...staffState, value: newValue })
      },
    })
  }, [affiliateId])

  const [emailState, setEmail] = useState<ManagedPageValue>({
    value: contactEmail,
    toggleValue: settingsTotoggleValue(settings, 'contactEmail'),
    toggleFn: (toggleValue: ToggleValue) => {
      setEmail({
        ...emailState,
        toggleValue: toggleValue,
      })
    },
    updateFn: (newValue: any) => {
      setEmail({ ...emailState, value: newValue })
    },
  })
  const [websiteState, setWebsiteState] = useState<ManagedPageValue>({
    value: web,
    toggleValue: settingsTotoggleValue(settings, 'web'),
    toggleFn: (toggleValue: ToggleValue) => {
      setWebsiteState({
        ...websiteState,
        toggleValue: toggleValue,
      })
    },
    updateFn: (newValue: any) => {
      setWebsiteState({ ...websiteState, value: newValue })
    },
  })
  const [addressState, setAddressState] = useState<ManagedPageValue>({
    value: address,
    toggleValue: settingsTotoggleValue(settings, 'address'),
    description:
      city +
      ', ' +
      (stateCode != 'Na' && stateCode != null ? stateCode : countryCode),
    toggleFn: (toggleValue: ToggleValue) => {
      setAddressState({
        ...addressState,
        toggleValue: toggleValue,
      })
    },
  })
  const [phoneState, setPhone] = useState<ManagedPageValue>({
    value: contactPhone,
    toggleValue: settingsTotoggleValue(settings, 'contactPhone'),
    toggleFn: (toggleValue: ToggleValue) => {
      setPhone({
        ...phoneState,
        toggleValue: toggleValue,
      })
    },
    updateFn: (newValue: any) => {
      setPhone({ ...phoneState, value: newValue })
    },
  })
  const [coachCredentials, setCoachCredentials] = useState<ManagedPageValue>({
    value: members,
    toggleValue: settingsTotoggleValue(settings, 'certifications'),
    toggleFn: (toggleValue: ToggleValue) => {
      setCoachCredentials({
        ...coachCredentials,
        toggleValue: toggleValue,
      })
    },
  })
  const [yearsAffiliated, setYearsAffiliated] = useState<ManagedPageValue>({
    value: new Date().getFullYear() - new Date(effectiveDate).getFullYear(),
    toggleValue: settingsTotoggleValue(settings, 'yearsAffiliated'),
    toggleFn: (toggleValue: ToggleValue) => {
      setYearsAffiliated({
        ...yearsAffiliated,
        toggleValue: toggleValue,
      })
    },
  })

  const [bioState, setBioState] = useState<ManagedPageValue>({
    value: bio,
    updateFn: (newValue: any) => {
      setBioState({ ...bioState, value: newValue })
    },
  })

  const [priamryImageUrlState, setPrimaryImageUrlState] =
    useState<ManagedPageValue>({
      value: images?.primary?.url,
      updateFn: (newValue: any) => {
        setPrimaryImageUrlState({ ...priamryImageUrlState, value: newValue })
      },
    })

  const [galleryImagesState, setGalleryImagesState] =
    useState<ManagedPageValue>({
      value:
        images?.gallery && images?.gallery.length
          ? [...images.gallery]?.sort((a, b) => a.order - b.order)
          : null,
      updateFn: (newValue: any) => {
        setGalleryImagesState({ ...galleryImagesState, value: newValue })
      },
    })

  const [scheduleImageUrlState, setScheduleImageUrlState] =
    useState<ManagedPageValue>({
      value: images?.schedule?.url,
      updateFn: (newValue: any) => {
        setScheduleImageUrlState({ ...scheduleImageUrlState, value: newValue })
      },
    })
  const [logoImageUrlState, setLogoImageUrlState] = useState<ManagedPageValue>({
    value: images?.logo?.url,
    updateFn: (newValue: any) => {
      setLogoImageUrlState({ ...logoImageUrlState, value: newValue })
    },
  })
  const [staffState, setStaffState] = useState<ManagedPageValue>({
    value: members?.sort((a, b) =>
      (a.firstName + a.lastName).toLowerCase() >
      (b.firstName + b.lastName).toLowerCase()
        ? -1
        : 1,
    ),
    updateFn: (newValue: any) => {
      setStaffState({ ...staffState, value: newValue })
    },
  })

  const [locationState, setLocationState] = useState<ManagedPageValue>({
    value: { lat, lng },
    updateFn: (newValue: any) => {
      setLocationState({ ...locationState, value: newValue })
    },
  })

  return {
    email: { get: emailState, set: setEmail },
    website: { get: websiteState, set: setWebsiteState },
    address: { get: addressState, set: setAddressState },
    phone: { get: phoneState, set: setPhone },
    coachCredentials: { get: coachCredentials, set: setCoachCredentials },
    yearsAffiliated: { get: yearsAffiliated, set: setYearsAffiliated },
    bio: { get: bioState, set: setBioState },
    primaryImageUrl: {
      get: priamryImageUrlState,
      set: setPrimaryImageUrlState,
    },
    logoImageUrl: {
      get: logoImageUrlState,
      set: setLogoImageUrlState,
    },
    galleryImages: { get: galleryImagesState, set: setGalleryImagesState },
    scheduleImageUrl: {
      get: scheduleImageUrlState,
      set: setScheduleImageUrlState,
    },
    location: { get: locationState, set: setLocationState },
    staff: { get: staffState, set: setStaffState },
  }
}

export const DirectoryPageContext = createContext({} as DirectoryPageContext)

export function DirectoryPageProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const directoryPage = useDirectoryPage()
  return (
    <DirectoryPageContext.Provider value={{ directoryPage }}>
      {children}
    </DirectoryPageContext.Provider>
  )
}
